<template>
  <Teleport to="#overlay">
    <transition name="transparency" appear>
      <div
        v-if="shouldRenderBg"
        class="sm-drawer-background"
        :style="backdropStyle"
        @click="global.closeTopDrawer()" />
    </transition>
  </Teleport>

  <Teleport to="#overlay">
    <transition name="fade" appear>
      <div
        v-if="visibility"
        class="sm-drawer"
        :class="props.class"
        :style="styleVars"
        :data-lvl="lvl">
        <div class="sm-drawer__header">
          <div class="sm-drawer__header__title">
            <slot name="title">
              <h1>{{ title }}</h1>
            </slot>
          </div>
          <div class="sm-drawer__header__close">
            <slot name="close">
              <SmButtonClose @close="visibility = !visibility" />
            </slot>
          </div>
        </div>
        <div class="sm-drawer__body">
          <slot></slot>
        </div>
        <div class="sm-drawer__footer">
          <slot name="footer"></slot>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, watch } from 'vue'
  import { storeToRefs } from 'pinia'
  import SmButtonClose from './SmButtonClose.vue'
  import { useGlobalStore } from '@/stores/globalStore'

  const global = useGlobalStore()
  const { drawers } = storeToRefs(global)

  const props = defineProps({
    visibility: { type: Boolean, default: true },
    title: { type: String, default: '' },
    class: { type: String, default: '' },
  })

  const emit = defineEmits(['update:visibility'])

  const visibility = computed({
    get: () => props.visibility,
    set: (value) => emit('update:visibility', value),
  })

  const drawerId = crypto.randomUUID()

  const register = () =>
    global.registerDrawer(drawerId, () => (visibility.value = false))
  const unregister = () => global.unregisterDrawer(drawerId)

  onBeforeUnmount(() => unregister())
  watch(visibility, (v) => (v ? register() : unregister()), {
    immediate: true,
  })

  const lvl = computed(() => global.getDrawerLevel(drawerId))

  const widthMap: Record<number, string> = {
    1: 'clamp(500px, 45vw, 600px)',
    2: 'clamp(450px, 40vw, 550px)',
    3: 'clamp(400px, 35vw, 500px)',
    4: 'clamp(380px, 32vw, 480px)',
    5: 'clamp(360px, 30vw, 460px)',
    6: 'clamp(340px, 28vw, 440px)',
    7: 'clamp(320px, 26vw, 420px)',
    8: 'clamp(300px, 24vw, 400px)',
    9: 'clamp(280px, 22vw, 380px)',
    10: 'clamp(260px, 20vw, 360px)',
  }

  const styleVars = computed(() => {
    const level = Math.max(1, lvl.value || 1)
    const z = 1500 + level * 2
    const w = widthMap[Math.min(level, 10)]
    return { '--sm-drawer-z': String(z), '--sm-drawer-w': w }
  })

  // ════════════════════════════════════════════════════════════════════════════
  //  Backdrop
  // ════════════════════════════════════════════════════════════════════════════

  const shouldRenderBg = computed(() => drawers.value[0] === drawerId)
  const backdropStyle = computed(() => {
    const level = Math.max(1, drawers.value.length)
    const z = 1500 + level * 2 - 1
    return { zIndex: String(z) }
  })
</script>

<style lang="scss" scoped>
  $padding: 20px;
  $drawer-height: 95vh;
  $drawer-padding-right: 15px;

  :deep(h1, h2) {
    margin: 0;
    line-height: 1;
  }

  /* BEGIN Transitions */
  .fade-enter-active,
  .fade-leave-active {
    transition: all 0.2s ease;
    transform: translateX(0px);
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateX(150px);
  }

  .transparency-enter-active,
  .transparency-leave-active {
    transition: all 0.2s ease;
  }

  .transparency-enter-from,
  .transparency-leave-to {
    opacity: 0;
  }
  /* END Transitions */

  /* Drawer Style */
  .sm-drawer {
    top: 0;
    right: 0;
    width: 100vw;
    height: 100vh;
    position: absolute;
    z-index: var(--sm-drawer-z, 1601);
    display: flex;
    flex-direction: column;
    padding: calc($padding / 2) $padding;

    @media screen and (min-width: 576px) {
      right: $drawer-padding-right;
      top: calc(50% - $drawer-height / 2);
      width: var(--sm-drawer-w, clamp(300px, 20vw, 400px));
      height: $drawer-height;
      border-radius: var(--border-radius);
    }

    // Glasmorphium
    background: var(--sm-elevation);
    box-shadow: 0 8px 32px 0 rgba(51, 51, 55, 0.37);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(8px);

    &__body {
      padding-right: $padding;
      margin: 0px auto;
      height: calc(100% - 2 * $padding);
      width: calc(100% - $padding);
      display: flex;
      flex-direction: column;
      overflow-y: auto;
    }

    &__header {
      width: calc(100% - $padding);
      margin: 0px auto;
      padding-top: 1.2rem;
      padding-bottom: 1.2rem;

      display: flex;
      flex-direction: row;
      justify-content: space-between;

      &__title {
        position: relative;
        display: flex;
        flex: 1;
        overflow: hidden;
        flex-direction: row;
        align-items: center;
        margin-right: auto;
        gap: 1rem;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          width: 80px;
          height: 100%;
          background: linear-gradient(
            to right,
            rgba(255, 255, 255, 0),
            var(--sm-elevation)
          );
        }
      }
      &__close {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 1rem;
      }
    }

    &__footer {
      width: calc(100% - $padding);
      margin: 0px auto calc($padding / 2);
      padding-top: 1.2rem;

      display: flex;
      flex-direction: row;
      justify-content: flex-end;
    }
  }
  /* Drawer Style */

  .sm-drawer-background {
    position: absolute;
    z-index: 1501; /* fallback, will be overwritten */
    left: 0;
    top: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(1px);
  }
</style>
